<template>
  <b-modal
    id="questionnaire-modal"
    :visible="questionnaireModal"
    size="lg"
    no-fade
    :no-close-on-backdrop="true"
    scrollable
    title="Compose questionnaire"
    header-text-variant="primary"
    ok-title="Accept"
    :ok-disabled="!canAccept"
    @ok="handleAddQuestion"
    @hide="resetData"
    @change="(val) => $emit('update:questionnaire-modal', val)"
  >
    <div style="min-height:480px">
      <b-row>
        <b-col cols="8">
          <b-form-input
            v-model="questionTitle"
            :placeholder="$t('messages.questionnaireModal.placeholders.questionPlaceholder')"
            class="title-item"
          />
        </b-col>
        <b-col
          class="d-flex justify-content-center align-items-center"
          style="max-width: 24px;"
        >
          <img
            :src="require('@hw-ui/assets/images/icons/image.png')"
            alt=""
          >
        </b-col>
        <b-col>
          <v-select
            v-model="typeQuestion"
            :placeholder="$t('messages.questionnaireModal.placeholders.typeOfQuestion')"
            :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
            style="text-transform: capitalize;min-width:160px;margin-left:0!important;"
            :clearable="false"
            :options="questionOptions"
            :reduce="(item) => item.value"
          />
        </b-col>
      </b-row>
  
      <div
        v-if="typeQuestion === TYPES.SINGLE || typeQuestion === TYPES.MULTIPLE"
        class="mt-4"
      >
        <b-form-radio-group>
          <b-row
            v-for="(answerOption, index) in answerOptions"
            :key="index"
            class="my-3"
          >
            <b-col
              cols="12"
              class="d-flex justify-content-between align-items-center"
            >
              <div
                v-if="answerOption.isEditing"
                class="flex-grow-1"
              >
                <b-form-input
                  v-model="answerOption.value"
                  autofocus
                  @keydown.enter="updateAnswerOption(index)"
                />
                <div class="mt-1">
                  <b-button
                    variant="primary"
                    :disabled="!answerOption.value"
                    @click="updateAnswerOption(index)"
                  >
                    {{ $t('save') }}
                  </b-button>
                  <b-button
                    variant="link-secondary"
                    @click="() => {
                      answerOption.value = answerOption.oldValue
                      answerOption.isEditing = false
                    }"
                  >
                    {{ $t('cancel') }}
                  </b-button>
                </div>
              </div>
              <div
                v-else
                class="d-flex flex-grow-1 align-items-center"
              >
                <b-form-radio
                  v-if="typeQuestion === TYPES.SINGLE"
                  :value="index"
                  disabled
                />
                <b-form-checkbox
                  v-else
                  disabled
                />
                <span
                  class="pl-1 flex-grow-1"
                  role="button"
                  @click="handleEditAnswerOption(index)"
                >
                  {{ answerOption.value }}
                </span>
              </div>
              <div v-if="!answerOption.isEditing">
                <feather-icon
                  icon="XIcon"
                  size="22"
                  class="cursor-pointer ml-1"
                  @click="removeAnswerOption(index)"
                />
              </div>
            </b-col>
          </b-row>
  
          <b-row v-if="showAddOptionForm">
            <b-col
              cols="12"
            >
              <b-form-input
                v-model="newOption"
                :placeholder="$t('messages.questionnaireModal.placeholders.addOption')"
                autofocus
                @keydown.enter="handleAddAnswerOption"
              />
              <div class="mt-1">
                <b-button
                  variant="primary"
                  :disabled="!newOption"
                  @click="handleAddAnswerOption"
                >
                  {{ $t('Add') }}
                </b-button>
                <b-button
                  variant="link-secondary"
                  @click="showAddOptionForm = false"
                >
                  {{ $t('cancel') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
  
          <b-row v-else>
            <b-col
              cols="12"
              class="d-flex justify-content-between align-items-center"
            >
              <div
                class="d-flex flex-grow-1 align-items-center"
              >
                <b-form-radio
                  v-if="typeQuestion === TYPES.SINGLE"
                  :value="-1"
                  disabled
                />
                <b-form-checkbox
                  v-else
                  disabled
                />
                <span
                  class="pl-1 flex-grow-1"
                  role="button"
                  @click="showAddOptionForm = true"
                >
                  {{ $t('messages.questionnaireModal.placeholders.addOption') }}
                </span>
              </div>
            </b-col>
          </b-row>
        </b-form-radio-group>
      </div>
  
      <div
        v-if="typeQuestion === `${TYPES.TEXT_SHORT}${TYPES.TEXT_LONG}`"
        class="mt-4"
      >
        <b-form-radio-group v-model="textQuestion">
          <b-row class="my-3">
            <b-col
              cols="3"
              class="d-flex justify-content-end align-items-center"
            >
              <b-form-radio
                :value="TYPES.TEXT_SHORT"
              >
                {{ $t('messages.questionnaireModal.radio.addShortAnswer') }}
              </b-form-radio>
            </b-col>
            <b-col
              cols="1"
              class="d-flex justify-content-center align-items-center"
            >
              {{ $t('messages.questionnaireModal.text.or') }}
            </b-col>
            <b-col
              cols="3"
              class="d-flex justify-content-start align-items-center"
            >
              <b-form-radio
                :value="TYPES.TEXT_LONG"
              >
                {{ $t('messages.questionnaireModal.radio.addLongAnswer') }}
              </b-form-radio>
            </b-col>
          </b-row>
        </b-form-radio-group>
      </div>
  
      <div
        v-if="typeQuestion === TYPES.DATE"
        class="mt-4"
      >
        <b-form-radio-group v-model="dateQuestion">
          <b-row class="my-3">
            <b-col
              cols="3"
              class="d-flex justify-content-start align-items-center"
            >
              <b-form-radio
                :value="TYPES.DATE"
              >
                {{ $t('messages.questionnaireModal.radio.datepickerAnswer') }}
              </b-form-radio>
            </b-col>
            <b-col
              cols="1"
              class="d-flex justify-content-center align-items-center"
            >
              {{ $t('messages.questionnaireModal.text.or') }}
            </b-col>
            <b-col
              cols="3"
              class="d-flex justify-content-start align-items-center"
            >
              <b-form-radio
                :value="TYPES.RANGE"
              >
                {{ $t('messages.questionnaireModal.radio.rangepickerAnswer') }}
              </b-form-radio>
            </b-col>
          </b-row>
        </b-form-radio-group>
      </div>
    </div>
  </b-modal>
</template>
  
  <script>
  import {
    VBTooltip,
    BModal,
    BRow,
    BCol,
    BFormInput,
    BFormRadioGroup,
    BFormRadio,
    BFormCheckbox,
    BButton,
  } from 'bootstrap-vue'
  import Ripple from 'vue-ripple-directive'
  import vSelect from 'vue-select'
  import { TYPES } from '@/shared/constants/questionnaire'
  
  export default {
    directives: {
      Ripple,
      'b-tooltip': VBTooltip,
    },
    components: {
      BModal,
      BRow,
      BCol,
      BFormRadioGroup,
      BFormRadio,
      BFormCheckbox,
      BFormInput,
      BButton,
  
      // 3rd Party
      vSelect,
    },
    model: {
      prop: 'questionnaireModal',
      event: 'update:questionnaire-modal',
    },
    props: {
      questionnaireModal: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        TYPES,
        questionOptions: [
          {
            label: this.$t('messages.questionnaireModal.labels.multipleChoice'),
            value: TYPES.MULTIPLE,
          },
          {
            label: this.$t('messages.questionnaireModal.labels.singleChoice'),
            value: TYPES.SINGLE,
          },
          {
            label: this.$t('messages.questionnaireModal.labels.textQuestion'),
            value: `${TYPES.TEXT_SHORT}${TYPES.TEXT_LONG}`,
          },
          {
            label: this.$t('messages.questionnaireModal.labels.dateQuestion'),
            value: TYPES.DATE,
          },
        ],
        typeQuestion: '',
        questionTitle: '',
        answerOptions: [],
        newOption: '',
        textQuestion: '',
        dateQuestion: '',
        showAddOptionForm: false,
      }
    },
    computed: {
      canAccept() {
        if (!this.typeQuestion || !this.questionTitle) {
          return false
        }
        if ((this.typeQuestion === TYPES.SINGLE || this.typeQuestion === TYPES.MULTIPLE)
        && this.answerOptions.length === 0) {
          return false
        }
        if (this.typeQuestion === `${TYPES.TEXT_SHORT}${TYPES.TEXT_LONG}` && !this.textQuestion) {
          return false
        }
        if (this.typeQuestion === TYPES.DATE && !this.dateQuestion) {
          return false
        }
        return true
      },
    },
    watch: {
      typeQuestion(newVal) {
        if (newVal === `${TYPES.TEXT_SHORT}${TYPES.TEXT_LONG}`) {
          this.textQuestion = ''
        } else {
          this.newOption = ''
        }
        this.answerOptions = []
      },
    },
    methods: {
      handleEditAnswerOption(index) {
        this.answerOptions[index].oldValue = this.answerOptions[index].value
        this.answerOptions[index].isEditing = true
      },
      updateAnswerOption(index) {
        if (!this.answerOptions[index].value) {
          return
        }
        this.answerOptions[index].isEditing = false
      },
      handleAddAnswerOption() {
        if (!this.newOption) {
          return
        }
        this.answerOptions.push({
          oldValue: '',
          value: this.newOption,
          isEditing: false,
        })
        this.newOption = ''
        this.showAddOptionForm = false
      },
      removeAnswerOption(index) {
        this.answerOptions.splice(index, 1)
      },
      handleAddQuestion() {
        let content = []
        let type = ''
        if (this.typeQuestion === TYPES.SINGLE || this.typeQuestion === TYPES.MULTIPLE) {
          type = this.typeQuestion
          content = this.answerOptions.map(option => ({ label: option.isEditing ? option.oldValue : option.value }))
        } else if (this.typeQuestion === TYPES.DATE) {
          type = this.dateQuestion
        } else {
          type = this.textQuestion
        }
        const data = {
          content,
          type,
          url: '',
          question: this.questionTitle,
        }
  
        this.$emit('add-question', data)
      },
      resetData() {
        this.typeQuestion = ''
        this.questionTitle = ''
        this.answerOptions = []
        this.newOption = ''
        this.textQuestion = ''
      },
    },
  }
  </script>
  